/**
 * Auto-refresh + pause-on-hidden controller, persisted to a tiny global
 * store so every page reads/writes the same state.
 */

import { useCallback, useEffect, useState } from "react";

interface RefreshState {
  intervalMs: number;
  paused: boolean;
  lastRefreshIso: string;
  tick: number;
}

const DEFAULT_INTERVAL_MS = 120_000;
let timerId: number | null = null;
let visibilityListenerAttached = false;

function emitTick() {
  store.set({
    lastRefreshIso: new Date().toISOString(),
    tick: store.state.tick + 1,
  });
}

function syncTimer() {
  if (typeof window === "undefined") return;
  if (timerId !== null) {
    window.clearInterval(timerId);
    timerId = null;
  }
  if (store.state.paused) return;
  timerId = window.setInterval(() => {
    if (document.hidden || store.state.paused) return;
    emitTick();
  }, store.state.intervalMs);
}

function ensureVisibilityListener() {
  if (typeof document === "undefined" || visibilityListenerAttached) return;
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && !store.state.paused) {
      emitTick();
    }
  });
  visibilityListenerAttached = true;
}

const store = {
  state: {
    intervalMs: DEFAULT_INTERVAL_MS,
    paused: false,
    lastRefreshIso: new Date().toISOString(),
    tick: 0,
  } as RefreshState,
  subs: new Set<() => void>(),
  set(p: Partial<RefreshState>) {
    const shouldSyncTimer = p.intervalMs != null || p.paused != null;
    store.state = { ...store.state, ...p };
    if (shouldSyncTimer) syncTimer();
    for (const cb of store.subs) cb();
  },
};

export function useAutoRefresh(): {
  intervalMs: number;
  paused: boolean;
  setPaused: (p: boolean) => void;
  lastRefreshIso: string;
  /** Subscribe to refresh ticks. Returns the current tick counter. */
  tick: number;
  triggerNow: () => void;
} {
  const [_, force] = useState(0);

  // Subscribe to store
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    store.subs.add(cb);
    ensureVisibilityListener();
    syncTimer();
    return () => { store.subs.delete(cb); };
  }, []);

  const setPaused = useCallback((p: boolean) => store.set({ paused: p }), []);
  const triggerNow = useCallback(() => emitTick(), []);

  return {
    intervalMs: store.state.intervalMs,
    paused: store.state.paused,
    setPaused,
    lastRefreshIso: store.state.lastRefreshIso,
    tick: store.state.tick,
    triggerNow,
  };
}
